import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TransactionRecord {
  id: string;
  source_type: string;
  payment_id: string | null;
  deal_id: string | null;
  contact_id: string | null;
  transaction_date: string;
  amount: number;
  currency: string;
  payment_method: string;
  status: string;
  stripe_status: string | null;
  visibility_status: string;
  trigger_type: string | null;
  transaction_type: string | null;
  reference_number: string | null;
  stripe_charge_id: string | null;
  stripe_payment_intent_id: string | null;
  notes: string | null;
  created_at: string;
  confirmed_at: string | null;
  rejection_reason: string | null;
  review_reason: string | null;
  has_materialized_ledger: boolean;
  // Joined fields
  contact_name: string;
  deal_name: string;
  recorded_by_name: string | null;
  reconciliation_status: 'matched' | 'unmatched';
  reconciled_at: string | null;
}

interface UseTransactionsReportOptions {
  startDate?: string;
  endDate?: string;
  paymentMethod?: string;
  status?: string;
  triggerType?: string;
}

function matchesStatusFilter(tx: TransactionRecord, statusFilter?: string) {
  if (!statusFilter || statusFilter === 'all') return true;

  switch (statusFilter) {
    case 'confirmed':
      return tx.status === 'confirmed' || tx.stripe_status === 'succeeded';
    case 'pending':
      return tx.status === 'pending' || tx.status === 'pending_confirmation';
    default:
      return tx.status === statusFilter;
  }
}

export function useTransactionsReport({
  startDate,
  endDate,
  paymentMethod,
  status,
  triggerType,
}: UseTransactionsReportOptions = {}) {
  return useQuery({
    queryKey: ['transactions-report', startDate, endDate, paymentMethod, status, triggerType],
    queryFn: async () => {
      const selectQuery = `
        id,
        source_type,
        payment_id,
        deal_id,
        contact_id,
        transaction_date,
        amount,
        currency,
        payment_method,
        ledger_status,
        stripe_status,
        visibility_status,
        trigger_type,
        transaction_type,
        reference_number,
        stripe_charge_id,
        stripe_payment_intent_id,
        notes,
        created_at,
        confirmed_at,
        rejection_reason,
        review_reason,
        first_name,
        last_name,
        deal_name,
        recorded_by_name,
        has_materialized_ledger
      `;

      let query = (supabase.from('v_finance_ledger_transactions' as any) as any)
        .select(selectQuery)
        .order('transaction_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (startDate) {
        query = query.gte('transaction_date', startDate);
      }
      if (endDate) {
        query = query.lte('transaction_date', endDate);
      }
      if (paymentMethod && paymentMethod !== 'all') {
        query = query.eq('payment_method', paymentMethod);
      }
      if (triggerType && triggerType !== 'all') {
        query = query.eq('trigger_type', triggerType);
      }

      const { data, error } = await query.limit(500);

      if (error) throw error;

      // Transform the data
      const result: TransactionRecord[] = ((data as any[]) || []).map((tx) => {
        return {
          id: tx.id,
          source_type: tx.source_type,
          payment_id: tx.payment_id ?? null,
          deal_id: tx.deal_id ?? null,
          contact_id: tx.contact_id ?? null,
          transaction_date: tx.transaction_date,
          amount: tx.amount,
          currency: tx.currency || 'USD',
          payment_method: tx.payment_method,
          status: tx.ledger_status,
          stripe_status: tx.stripe_status ?? null,
          visibility_status: tx.visibility_status,
          trigger_type: tx.trigger_type,
          transaction_type: tx.transaction_type,
          reference_number: tx.reference_number,
          stripe_charge_id: tx.stripe_charge_id,
          stripe_payment_intent_id: tx.stripe_payment_intent_id,
          notes: tx.notes,
          created_at: tx.created_at,
          confirmed_at: tx.confirmed_at,
          rejection_reason: tx.rejection_reason,
          review_reason: tx.review_reason ?? null,
          has_materialized_ledger: Boolean(tx.has_materialized_ledger),
          contact_name: `${tx.first_name || ''} ${tx.last_name || ''}`.trim() || '-',
          deal_name: tx.deal_name || '-',
          recorded_by_name: tx.recorded_by_name || null,
          reconciliation_status: 'unmatched',
          reconciled_at: null,
        };
      });

      const txIds = result.map((tx) => tx.id);
      if (txIds.length > 0) {
        const { data: matchRows, error: matchError } = await (supabase.from('finance_recon_matches' as any) as any)
          .select('payment_transaction_id,status,matched_at')
          .in('payment_transaction_id', txIds)
          .eq('status', 'matched')
          .order('matched_at', { ascending: false });

        if (matchError) throw matchError;

        const byTx = new Map<string, { matched_at: string | null }>();
        ((matchRows || []) as any[]).forEach((row) => {
          const txId = row.payment_transaction_id as string | null;
          if (!txId || byTx.has(txId)) return;
          byTx.set(txId, { matched_at: row.matched_at || null });
        });

        result.forEach((tx) => {
          const row = byTx.get(tx.id);
          if (!row) return;
          tx.reconciliation_status = 'matched';
          tx.reconciled_at = row.matched_at;
        });
      }

      return result.filter((tx) => matchesStatusFilter(tx, status));
    },
  });
}
